import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/seo";
import { FiHome, FiArrowRight, FiDollarSign, FiPieChart, FiCheck, FiAlertCircle } from "@/lib/icons";
import { usePropertyBudgetCalculator } from "./_hooks/usePropertyBudgetCalculator";
import { AUSTRALIAN_STATES, STATE_NAMES, type AustralianState } from "./_types/calculator.types";
import { cn } from "@/lib/utils";

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step,
  formatValue,
  subtext,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  formatValue: (v: number) => string;
  subtext?: string;
}) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div>
          <label className="text-sm font-medium">{label}</label>
          {subtext && <p className="text-xs text-muted-foreground">{subtext}</p>}
        </div>
        <span className="text-lg font-bold text-primary">{formatValue(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
      />
    </div>
  );
}

export function PropertyBudgetCalculatorPage() {
  const { inputs, results, updateInput } = usePropertyBudgetCalculator();

  const lvrOptions = [
    { value: 80, label: "80% (No LMI)" },
    { value: 85, label: "85%" },
    { value: 90, label: "90%" },
    { value: 95, label: "95%" },
  ];

  return (
    <>
      <SEO
        title="Property Budget Calculator"
        description="Find out what property you can afford based on your savings, income, and borrowing capacity."
        url="/calculators/property-budget"
      />

      <section className="bg-cream py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex p-3 bg-accent/20 rounded-xl mb-6">
              <FiHome className="size-6 text-primary" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
              What Can I Afford?
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Calculate your maximum property budget based on your savings, income, and
              preferred loan-to-value ratio. We'll factor in stamp duty, LMI, and other costs.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Savings & Income */}
            <div className="bg-background rounded-3xl border border-border p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-cream rounded-lg">
                  <FiDollarSign className="size-5 text-primary" />
                </div>
                <h2 className="font-medium">Your Finances</h2>
              </div>

              <div className="space-y-6">
                <SliderInput
                  label="Total Savings"
                  value={inputs.totalSavings}
                  onChange={(v) => updateInput("totalSavings", v)}
                  min={20000}
                  max={500000}
                  step={5000}
                  formatValue={formatCurrency}
                />

                <SliderInput
                  label="Keep in Reserve"
                  value={inputs.keepInReserve}
                  onChange={(v) => updateInput("keepInReserve", v)}
                  min={0}
                  max={50000}
                  step={1000}
                  formatValue={formatCurrency}
                  subtext="Emergency fund / buffer"
                />

                <div className="pt-4 border-t border-border">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Available for purchase</span>
                    <span className="font-bold text-primary">
                      {formatCurrency(results.availableDeposit)}
                    </span>
                  </div>
                </div>

                <SliderInput
                  label="Gross Annual Income"
                  value={inputs.grossIncome}
                  onChange={(v) => updateInput("grossIncome", v)}
                  min={40000}
                  max={500000}
                  step={5000}
                  formatValue={formatCurrency}
                />

                <SliderInput
                  label="Partner Income"
                  value={inputs.partnerIncome}
                  onChange={(v) => updateInput("partnerIncome", v)}
                  min={0}
                  max={500000}
                  step={5000}
                  formatValue={formatCurrency}
                />

                <div className="pt-4 border-t border-border">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Est. Borrowing Power</span>
                    <span className="font-bold text-primary">
                      {formatCurrency(results.estimatedBorrowingPower)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Loan Preferences */}
            <div className="bg-background rounded-3xl border border-border p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-cream rounded-lg">
                  <FiPieChart className="size-5 text-primary" />
                </div>
                <h2 className="font-medium">Loan Preferences</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="text-sm font-medium mb-3 block">State</label>
                  <select
                    value={inputs.state}
                    onChange={(e) => updateInput("state", e.target.value as AustralianState)}
                    className="w-full p-3 rounded-lg border border-border bg-background"
                  >
                    {AUSTRALIAN_STATES.map((state) => (
                      <option key={state} value={state}>
                        {STATE_NAMES[state]}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-3 block">Loan to Value Ratio (LVR)</label>
                  <div className="grid grid-cols-2 gap-2">
                    {lvrOptions.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => updateInput("preferredLVR", opt.value)}
                        className={cn(
                          "p-3 rounded-lg text-sm font-medium transition-colors",
                          inputs.preferredLVR === opt.value
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground hover:bg-muted/80"
                        )}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <SliderInput
                  label="Interest Rate"
                  value={inputs.interestRate}
                  onChange={(v) => updateInput("interestRate", v)}
                  min={4}
                  max={9}
                  step={0.1}
                  formatValue={(v) => `${v.toFixed(2)}%`}
                />

                <SliderInput
                  label="Loan Term"
                  value={inputs.loanTerm}
                  onChange={(v) => updateInput("loanTerm", v)}
                  min={10}
                  max={30}
                  step={1}
                  formatValue={(v) => `${v} years`}
                />

                <label className="flex items-center gap-3 cursor-pointer pt-4 border-t border-border">
                  <input
                    type="checkbox"
                    checked={inputs.isFirstHomeBuyer}
                    onChange={(e) => updateInput("isFirstHomeBuyer", e.target.checked)}
                    className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-sm font-medium">First Home Buyer</span>
                </label>
              </div>
            </div>

            {/* Cost Breakdown */}
            <div className="space-y-6">
              <div className="bg-background rounded-3xl border border-border p-6">
                <h3 className="font-medium mb-4">Upfront Costs Breakdown</h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Stamp Duty</span>
                    <span className="font-medium">{formatCurrency(results.stampDuty)}</span>
                  </div>
                  {results.lmiCost > 0 && (
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-muted-foreground">LMI</span>
                      <span className="font-medium text-destructive">
                        {formatCurrency(results.lmiCost)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Other Costs</span>
                    <span className="font-medium">{formatCurrency(results.otherCosts)}</span>
                  </div>
                  <div className="flex justify-between py-2 font-bold">
                    <span>Total</span>
                    <span>{formatCurrency(results.totalUpfrontCosts)}</span>
                  </div>
                </div>
              </div>

              {/* Affordability Check */}
              <div
                className={cn(
                  "rounded-2xl p-6",
                  results.isAffordable ? "bg-accent/10" : "bg-destructive/10"
                )}
              >
                {results.isAffordable ? (
                  <div className="flex items-start gap-3">
                    <FiCheck className="size-5 text-accent mt-0.5" />
                    <div>
                      <p className="font-medium text-primary">Looks affordable!</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Your repayment is less than 30% of your gross income
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <FiAlertCircle className="size-5 text-destructive mt-0.5" />
                    <div>
                      <p className="font-medium text-destructive">Consider a lower budget</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Your repayment exceeds 30% of gross income. We recommend{" "}
                        {formatCurrency(results.recommendedPrice)}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* CTA */}
              <div className="bg-primary text-primary-foreground rounded-2xl p-6 text-center">
                <p className="text-primary-foreground/90 mb-2">Ready to start your search?</p>
                <p className="font-serif text-xl mb-4">Get pre-approved today</p>
                <Button
                  asChild
                  className="btn-pill bg-accent text-accent-foreground hover:bg-accent/90"
                >
                  <Link to="/contact">
                    Get in Touch
                    <FiArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Main Result - placed after inputs */}
          <div className="bg-primary text-primary-foreground rounded-3xl p-8 md:p-10 max-w-3xl mx-auto mt-12">
            <div className="text-center">
              <p className="text-primary-foreground/90 mb-2">Your maximum purchase price</p>
              <p className="text-5xl md:text-6xl font-bold mb-4">
                {formatCurrency(results.maxPurchasePrice)}
              </p>
              {!results.isAffordable && (
                <div className="inline-flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-full text-sm">
                  <FiAlertCircle className="size-4" />
                  Recommended: {formatCurrency(results.recommendedPrice)}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-8 border-t border-primary-foreground/20">
              <div className="text-center">
                <p className="text-primary-foreground/85 text-xs mb-1">Loan Amount</p>
                <p className="font-semibold">{formatCurrency(results.loanAmount)}</p>
              </div>
              <div className="text-center">
                <p className="text-primary-foreground/85 text-xs mb-1">LVR</p>
                <p className="font-semibold">{results.effectiveLVR}%</p>
              </div>
              <div className="text-center">
                <p className="text-primary-foreground/85 text-xs mb-1">Monthly Repayment</p>
                <p className="font-semibold">{formatCurrency(results.monthlyRepayment)}</p>
              </div>
              <div className="text-center">
                <p className="text-primary-foreground/85 text-xs mb-1">Upfront Costs</p>
                <p className="font-semibold">{formatCurrency(results.totalUpfrontCosts)}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-6">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> This calculator provides estimates only. Actual borrowing capacity
              depends on many factors including credit history, employment type, existing debts, and
              lender criteria. Contact us for an accurate pre-approval assessment.
            </p>
            <p className="text-sm text-muted-foreground">
              <strong>Disclaimer:</strong> This page provides general information only and has been
              prepared without considering your objectives, financial situation or needs. We recommend
              that you consider whether it is appropriate for your circumstances and your full financial
              situation will need to be reviewed prior to acceptance of any offer or product. It does
              not constitute legal, tax or financial advice and you should always seek professional
              advice in relation to your individual circumstances.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
